import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Check, Loader, Plus, ShoppingBag, TriangleAlert, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import groupBy from 'lodash/groupBy';
import { useRef, useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import dayPickerStyles from 'react-day-picker/style.css?url';
import type { ChangeEvent } from 'react';
import type { LastOrder, OrderPayload } from '@/lib/types/order.types.ts';
import { OrderDefaults } from '@/lib/types/order.types.ts';
import { COUNTRY_CONFIGS, usePhoneInputMask } from '@/lib/hooks/use-phone-input-mask.ts';
import cartStore from '@/stores/cart.store.ts';
import orderStore from '@/stores/order.store.ts';
import { addOrder } from '@/lib/server-fns.ts';
import Section from '@/components/layout/section.tsx';
import Breadcrumbs from '@/components/layout/breadcrumbs.tsx';

export const Route = createFileRoute('/order-service')({
	component: OrderService,
	head: () => ({
		links: [
			{
				rel: 'stylesheet',
				href: dayPickerStyles,
			},
		],
	}),
});

const { VITE_LOCALE } = import.meta.env;

function OrderService() {
	const [errors, setErrors] = useState<Array<string>>([]);
	const [isFormSubmitPending, setIsFormSubmitPending] = useState(false);

	const requiredRef = useRef<HTMLDivElement>(null);

	const [values, setValues] = useState({
		name: '',
		email: '',
		year: '',
		brand: '',
		model: '',
		license: '',
		company_order: false,
	});

	const validators = {
		email: (value: string) => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value),
		name: (value: string) => value.trim().length >= 2,
		phone: (value: string) => value.trim().length > 10,
	};

	const { t } = useTranslation();
	const navigate = useNavigate();

	const phone = usePhoneInputMask();

	const { items, deleteItem, resetCart } = cartStore(
		useShallow((state) => ({
			items: state.items,
			deleteItem: state.deleteItem,
			resetCart: state.resetCart,
		})),
	);

	const { setLastOrder, setSelectedService } = orderStore(
		useShallow((state) => ({
			setLastOrder: state.setLastOrder,
			setSelectedService: state.setSelectedService,
		})),
	);

	const currencyFormatter = new Intl.NumberFormat(VITE_LOCALE, {
		style: 'currency',
		currency: VITE_LOCALE === 'pl' ? 'PLN' : 'PLN',
	});

	const data = groupBy(items, 'service');

	const total = items?.reduce((acc, item) => acc + item?.price, 0);
	const totalFormatted = currencyFormatter.format(total);

	const getCountryInfo = () => {
		if (!phone.countryCode) return null;
		const code = phone.countryCode.replace('+', '');
		return COUNTRY_CONFIGS[code];
	};

	const countryInfo = getCountryInfo();

	const date = new Date();
	const year = date.getFullYear();

	const validateForm = () => {
		const isEmailValid = validators?.email(values?.email);
		if (isEmailValid) {
			setErrors((prev) => prev?.filter((e) => e !== 'email'));
		} else {
			setErrors((prev) => (prev?.includes('email') ? prev : [...prev, 'email']));
		}

		const isPhoneValid = validators?.phone(phone.rawValue);
		if (isPhoneValid) {
			setErrors((prev) => prev?.filter((e) => e !== 'phone'));
		} else {
			setErrors((prev) => (prev?.includes('phone') ? prev : [...prev, 'phone']));
		}

		const isNameValid = validators?.name(values?.name);

		if (isNameValid) {
			setErrors((prev) => prev?.filter((e) => e !== 'name'));
		} else {
			setErrors((prev) => (prev?.includes('name') ? prev : [...prev, 'name']));
		}

		return isEmailValid && isPhoneValid && isNameValid;
	};

	const handleValueChange = (event: any) => {
		const name: any = event?.target?.name;
		const type: any = event?.target?.type;

		const value = {
			checkbox: event.target.checked,
			text: event.target?.value,
			email: event.target?.value,
			'select-one': event.target?.value,
		} as any;

		setValues((prev) => ({ ...prev, [name]: value?.[type] }));

		if (!errors?.length) return;
		validateForm();
	};

	const handleChangePhone = (event: ChangeEvent<HTMLInputElement>) => {
		phone.onChange(event);
		if (!errors?.length) return;
		validateForm();
	};

	const handleSubmit = async (evt: any) => {
		evt.preventDefault();

		const isFormValid = validateForm();

		if (!isFormValid) {
			requiredRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			return;
		}

		const subServices = items?.map((item) => ({
			carSubServiceId: item?.id,
			startDate: `${item?.date?.day}T${item?.date?.slot?.startTime}`,
		}));

		const payload: OrderPayload = {
			fullName: values?.name,
			vehicleBrand: values?.brand || OrderDefaults.NotSpecified,
			vehicleModel: values?.model || OrderDefaults.NotSpecified,
			phoneNumber: `${phone.countryCode}${phone?.localNumber}`,
			countryPhoneCode: phone.countryCode,
			email: values?.email,
			carYear: values?.year ? parseInt(values.year) : 1900,
			carLicense: values?.license || OrderDefaults.NotSpecified,
			isCompanyOrder: !!values?.company_order,
			language: VITE_LOCALE,
			subServicesDto: subServices,
			additionalInfoOrNull: null,
			isInvoice: true,
			paymentMethod: 'cash',
			homePageId: 1,
		};

		try {
			setIsFormSubmitPending(true);
			const response = (await addOrder({ data: { payload } })) as LastOrder['response'];

			const result: LastOrder = {
				payload,
				services: items,
				response,
			};

			setLastOrder(result);
			resetCart();
			void navigate({ to: '/order-success' });
		} catch (error) {
			console.error(error);
			toast.error(error as string, { richColors: true });
		} finally {
			setIsFormSubmitPending(false);
		}
	};

	const isOrderButtonDisabled = !items?.every((item) => item?.date) || !items?.length;

	return (
		<Section className="!py-0 max-lg:!px-0">
			<form
				className="w-full h-[calc(100svh_-_163px)] max-lg:h-auto flex max-lg:flex-col border-l border-gray-300 max-lg:border-l-0 "
				onSubmit={handleSubmit}
			>
				<Section className="!py-4 min-lg:hidden">
					<Breadcrumbs
						items={[
							{ title: t('global.menu.home'), link: '/' },
							{ title: t('order.checkout_title') },
						]}
					/>
				</Section>

				<div className="w-[calc(100%_-_500px)] order-1 max-lg:order-3 h-full flex flex-col justify-start py-4 px-6 max-sm:px-4 gap-4 m-auto max-lg:w-full overflow-auto">
					<div className="w-full max-w-[800px] mx-auto flex flex-col gap-4">
						<Section className="!px-0 max-lg:hidden">
							<Breadcrumbs
								items={[
									{ title: t('global.menu.home'), link: '/' },
									{ title: t('order.checkout_title') },
								]}
							/>
						</Section>

						<div className="flex flex-col mt-auto">
							<h2 className="text-[var(--primary)] text-2xl font-medium mb-2">
								{t('order.required_info')}
							</h2>

							<div
								className="flex flex-col gap-2 p-3 border-2 rounded-xl border-neutral-200 max-lg:border-0 max-lg:p-0"
								ref={requiredRef}
							>
								<div className="w-full relative">
									<input
										className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600 p-4 pl-4 border-2 border-neutral-100 pr-10 data-[error=true]:border-red-400"
										placeholder={t('order.placeholders.name')}
										maxLength={40}
										name="name"
										onChange={handleValueChange}
										value={values?.name || ''}
										data-error={errors?.includes('name')}
										disabled={isFormSubmitPending}
									/>

									<div className="absolute flex items-center justify-center right-0 top-0 w-12 h-12">
										<span className="mt-1 text-red-600 text-xl">*</span>
									</div>
								</div>

								<div className="w-full relative">
									<div
										className="absolute w-12 h-12 flex items-center justify-center text-xl transition-all opacity-0 invisible data-[visible=true]:opacity-100 data-[visible=true]:visible"
										data-visible={!!countryInfo}
									>
										{countryInfo?.flag}
									</div>

									<input
										type="text"
										value={phone.value}
										onChange={handleChangePhone}
										onFocus={phone.onFocus}
										onBlur={phone.onBlur}
										onKeyDown={phone.onKeyDown}
										className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600 transition-all p-4 pl-4 border-2 border-neutral-100 pr-10  data-[country=true]:pl-12 data-[error=true]:border-red-400"
										placeholder={t('order.placeholders.phone')}
										data-country={!!countryInfo}
										maxLength={40}
										name="phone"
										data-error={errors?.includes('phone')}
										disabled={isFormSubmitPending}
									/>

									<div className="absolute flex items-center justify-center right-0 top-0 w-12 h-12">
										<span className="mt-1 text-red-600 text-xl">*</span>
									</div>
								</div>

								<div className="w-full relative">
									<input
										type="text"
										className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600 p-4 pl-4 border-2 border-neutral-100 pr-10  data-[error=true]:border-red-400"
										placeholder={t('order.placeholders.email')}
										maxLength={40}
										name="email"
										onChange={handleValueChange}
										value={values?.email || ''}
										data-error={errors?.includes('email')}
										disabled={isFormSubmitPending}
									/>

									<div className="absolute flex items-center justify-center right-0 top-0 w-12 h-12">
										<span className="mt-1 text-red-600 text-xl">*</span>
									</div>
								</div>
							</div>
						</div>

						<div className="flex flex-col mb-auto">
							<h2 className="text-neutral-500 text-2xl font-medium mb-2">
								{t('order.optional_info')}
							</h2>

							<div className="flex flex-col gap-2 p-3 border-2 rounded-xl border-neutral-200 max-lg:border-0 max-lg:p-0">
								<input
									className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600 p-4 pl-4 border-2 border-neutral-100 "
									placeholder={t('order.placeholders.brand')}
									maxLength={40}
									name="brand"
									onChange={handleValueChange}
									value={values?.brand || ''}
									disabled={isFormSubmitPending}
								/>

								<input
									className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600 p-4 pl-4 border-2 border-neutral-100 "
									placeholder={t('order.placeholders.model')}
									maxLength={40}
									name="model"
									onChange={handleValueChange}
									value={values?.model || ''}
									disabled={isFormSubmitPending}
								/>

								<div className="select">
									<select
										id="countries"
										className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600  pl-4 pr-10 border-2 border-neutral-100  appearance-none"
										name="year"
										onChange={handleValueChange}
										value={values?.year || ''}
										disabled={isFormSubmitPending}
									>
										<option defaultValue={t('order.placeholders.year')}>
											{t('order.placeholders.year')}
										</option>

										{Array.from({ length: year - 1979 })
											?.map((_, i) => 1980 + i)
											?.map((val, i) => (
												<option key={`${val}-${i}`} value={`${val}`}>
													{val}
												</option>
											))
											?.reverse()}
									</select>
								</div>

								<input
									className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600 p-4 pl-4 border-2 border-neutral-100 "
									placeholder={t('order.placeholders.license')}
									maxLength={40}
									name="license"
									onChange={handleValueChange}
									value={values?.license || ''}
									disabled={isFormSubmitPending}
								/>

								<label
									htmlFor="company-order"
									className="w-fit flex items-center gap-2 p-1 select-none cursor-pointer"
								>
									<input
										id="company-order"
										type="checkbox"
										className="w-5 h-5 accent-[var(--primary)]"
										name="company_order"
										onChange={handleValueChange}
										checked={values?.company_order || false}
										disabled={isFormSubmitPending}
									/>
									<span>{t('order.placeholders.company_order')}</span>
								</label>
							</div>
						</div>
					</div>
				</div>

				<div className="w-[500px] h-full border-l border-r border-gray-300 max-lg:border-r-0 max-lg:w-full max-lg:border-l-0 order-2 max-lg:order-1">
					<div className="flex flex-col w-full h-full bg-white transition-all">
						<div className="w-full flex items-center justify-between p-4 px-6 max-sm:px-4 gap-2 border-b relative border-neutral-200 max-sm:flex-wrap">
							<h2 className="text-2xl text-[var(--primary)]">{t('order.checkout_title')}</h2>

							<button
								className="h-10 flex items-center gap-2 px-3 justify-center max-lg:hidden bg-green-600 rounded-xl text-white goldman max-sm:w-full font-medium text-md cursor-pointer active:scale-97 hover:brightness-95 disabled:bg-neutral-400 disabled:pointer-events-none"
								type="button"
								onClick={() => {
									setSelectedService({});
								}}
							>
								<span>{t('order.add_service')}</span>

								<Plus size="1.25rem" />
							</button>
						</div>

						<div className="flex-1 w-full max-w-[800px] mx-auto overflow-y-auto p-2 px-6 max-sm:p-4 gap-2 flex flex-col">
							{items?.length ? (
								Object.entries(data)?.map(([service, value], index) => {
									return (
										<div className="w-full flex flex-col gap-2" key={`${service}-${index}`}>
											<span className="font-medium text-lg text-[var(--primary)] truncate">
												{service}
											</span>

											{value?.map((item) => {
												const price = currencyFormatter.format(item?.price);

												return (
													<div key={item.id} className="w-full p-3 bg-neutral-100 rounded-xl">
														<div className="flex flex-col gap-2">
															<div className="flex items-center justify-start gap-4">
																<div className="w-16 h-16 shrink-0 flex items-center justify-center bg-neutral-200 rounded-xl overflow-hidden">
																	<img
																		className="w-full h-full object-contain data-[cover=true]:object-cover"
																		src={item?.serviceImage || '/icons/service.png'}
																		alt={item?.name || '•••••••••••••••'}
																		data-cover={!!item?.serviceImage}
																	/>
																</div>

																<div className="flex flex-col gap-1">
																	<Link
																		to="/services/$serviceId"
																		params={{
																			serviceId: `${item?.serviceId}`,
																		}}
																		style={{ viewTransitionName: `sub-service-name-${item?.id}` }}
																		className="text-sm font-bold underline text-neutral-700 truncate max-w-[280px] max-md:max-w-[220px] max-sm:max-w-[160px]"
																	>
																		{item?.name}
																	</Link>

																	<span className="text-sm text-neutral-500">
																		{t('services.sub_service_start_from', { qty: price })}
																	</span>
																</div>

																<button
																	className="bg-red-100 ml-auto p-2 rounded-xl transition-all cursor-pointer text-red-800 hover:bg-red-200 active:scale-97 disabled:pointer-events-none disabled:bg-neutral-200"
																	onClick={() => {
																		deleteItem(item.id);
																	}}
																	type="button"
																	disabled={isFormSubmitPending}
																>
																	<X
																		size="1.25rem"
																		className="data-[disabled=true]:text-neutral-400"
																		data-disabled={isFormSubmitPending}
																	/>
																</button>
															</div>

															<div className="w-full">
																{item?.date ? (
																	<button
																		className="text-[13px] flex items-center justify-between gap-2 py-1.5 px-2 transition-all bg-green-400/10 font-normal text-neutral-600 rounded-lg w-full cursor-pointer active:scale-97 hover:bg-green-400/20 disabled:pointer-events-none disabled:bg-neutral-200"
																		onClick={() => setSelectedService(item)}
																		type="button"
																		disabled={isFormSubmitPending}
																	>
																		<span className="capitalize">{`${dayjs(item?.date?.day).locale(VITE_LOCALE).format('MMMM, ddd DD')} • ${item?.date?.slot?.startTime} - ${item?.date?.slot?.endTime}`}</span>

																		<Check
																			size="1.25rem"
																			className="text-green-600 data-[disabled=true]:text-neutral-400"
																			data-disabled={isFormSubmitPending}
																		/>
																	</button>
																) : (
																	<button
																		className="w-full text-neutral-700 flex items-center justify-center gap-2 text-[13px] cursor-pointer rounded-lg py-1.5 px-2 bg-[var(--primary)]/20 active:scale-97 hover:brightness-95 disabled:pointer-events-none disabled:bg-neutral-200"
																		onClick={() => setSelectedService(item)}
																		type="button"
																		disabled={isFormSubmitPending}
																	>
																		{t('order.choose_date')}

																		<TriangleAlert size="1rem" />
																	</button>
																)}
															</div>
														</div>
													</div>
												);
											})}
										</div>
									);
								})
							) : (
								<div className="flex flex-1 items-center justify-center flex-col gap-2 min-h-60">
									<ShoppingBag size="5rem" className="text-neutral-200" />
									<p className="text-neutral-400 text-lg max-md:text-[16px]">{t('cart.empty')}</p>
								</div>
							)}
						</div>

						<div className="w-full p-4 px-6 max-sm:px-4 max-lg:hidden flex flex-col items-center justify-between gap-2 border-t relative border-neutral-200">
							<p className="text-[var(--primary)] text-xl font-medium">
								{total
									? t('services.sub_service_start_from', { qty: totalFormatted })
									: totalFormatted}
							</p>

							<button
								className="w-full flex items-center  justify-center p-2 px-4 bg-[var(--primary)] rounded-xl text-white goldman font-medium text-xl cursor-pointer active:scale-97 hover:brightness-95 disabled:bg-neutral-400 disabled:pointer-events-none"
								type="submit"
								disabled={isOrderButtonDisabled || isFormSubmitPending}
							>
								{isFormSubmitPending ? (
									<Loader className="animate-spin" />
								) : (
									t('order.submit_button')
								)}
							</button>
						</div>
					</div>
				</div>

				<div className="w-full max-w-[800px] mx-auto py-2 px-6 flex items-center max-sm:px-4 order-2 min-lg:hidden">
					<button
						className="w-full h-10 flex items-center gap-2 px-3 justify-center bg-green-600 rounded-xl text-white goldman max-sm:w-full font-medium text-md cursor-pointer active:scale-97 hover:brightness-95 disabled:bg-neutral-400 disabled:pointer-events-none"
						type="button"
						onClick={() => {
							setSelectedService({});
						}}
					>
						<span>{t('order.add_service')}</span>

						<Plus size="1.25rem" />
					</button>
				</div>

				<div className="w-full p-4 px-6 max-sm:px-4 min-lg:hidden flex flex-col items-center justify-between gap-2 border-t relative border-neutral-200 order-4">
					<p className="text-[var(--primary)] text-xl font-medium">
						{total ? t('services.sub_service_start_from', { qty: totalFormatted }) : totalFormatted}
					</p>

					<button
						className="w-full flex items-center justify-center p-2 px-4 bg-[var(--primary)] rounded-xl text-white goldman font-medium text-xl cursor-pointer active:scale-97 hover:brightness-95 disabled:bg-neutral-400 disabled:pointer-events-none"
						type="submit"
						disabled={isOrderButtonDisabled || isFormSubmitPending}
					>
						{isFormSubmitPending ? <Loader className="animate-spin" /> : t('order.submit_button')}
					</button>
				</div>
			</form>
		</Section>
	);
}
